import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CheckCircle2, Loader2, XCircle, MailWarning } from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { api, extractAuthPayload, isSuccessResponse } from "lib/api";
import { apiURl } from "store/actions";
import { SetAuthUserData } from "store/reducer";
import { validateEmail } from "lib/validation";
import swtAlert from "lib/sweetAlert";

const STATUS = {
  PENDING: "pending",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
  MISSING_TOKEN: "missing-token",
};

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = searchParams.get("token");

  const [status, setStatus] = useState(token ? STATUS.PENDING : STATUS.MISSING_TOKEN);
  const [message, setMessage] = useState("");
  const [autoLoggedIn, setAutoLoggedIn] = useState(false);
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    // Guard against React StrictMode double-invoke in dev so the verify
    // endpoint isn't hit twice with the same one-time token. The ref persists
    // across the StrictMode remount, so the second run is a no-op.
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const verify = async () => {
      setStatus(STATUS.LOADING);
      try {
        const resp = await api.post(
          apiURl.verifyEmail,
          { token },
          { auth: false }
        );

        if (!isSuccessResponse(resp)) {
          setStatus(STATUS.ERROR);
          setMessage(resp?.message || "We couldn't verify your email link.");
          return;
        }

        const auth = extractAuthPayload(resp);
        if (auth.token) {
          dispatch(SetAuthUserData(auth));
          setAutoLoggedIn(true);
        }

        setStatus(STATUS.SUCCESS);
        setMessage(resp?.message || "Your email has been verified.");
        swtAlert.success(
          "Email Verified",
          resp?.message || "Email verified successfully"
        );
      } catch (err) {
        setStatus(STATUS.ERROR);
        setMessage(
          err?.message ||
            "This verification link is invalid or has expired."
        );
      }
    };

    verify();
  }, [token, dispatch]);

  return (
    <div className="container mx-auto flex flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-12 xl:px-16">
      <div className="w-full max-w-md text-center">
        <StatusIcon status={status} />

        <h1 className="mt-6 text-2xl font-semibold">{titleFor(status)}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {messageFor(status, message)}
        </p>

        <div className="mt-8 flex flex-col gap-3">
          {status === STATUS.SUCCESS && autoLoggedIn && (
            <Button onClick={() => navigate("/")}>
              Continue to Home
            </Button>
          )}

          {status === STATUS.SUCCESS && !autoLoggedIn && (
            <Button onClick={() => navigate("/sign-in")}>
              Sign In
            </Button>
          )}

          {(status === STATUS.ERROR || status === STATUS.MISSING_TOKEN) && (
            <>
              <ResendVerificationForm />

              <div className="flex items-center gap-3">
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs uppercase tracking-wider text-muted-foreground">
                  Or
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>

              <Button asChild variant="outline">
                <Link to="/sign-in">Go to Sign In</Link>
              </Button>
              <Button asChild variant="ghost">
                <Link to="/sign-up">Create a New Account</Link>
              </Button>
            </>
          )}

          {status === STATUS.LOADING && (
            <p className="text-xs text-muted-foreground">
              Hang tight, this should only take a moment.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ResendVerificationForm() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sentTo, setSentTo] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");

    setIsSending(true);
    try {
      const resp = await api.post(
        apiURl.resendVerification,
        { email: email.trim() },
        { auth: false }
      );

      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Couldn't send verification email";
        setError(msg);
        swtAlert.error("Resend Failed", msg);
        return;
      }

      setSentTo(email.trim());
      swtAlert.success(
        "Verification Email Sent",
        resp?.message || "Verification email sent"
      );
    } catch (err) {
      const msg = err?.message || "Couldn't send verification email";
      setError(msg);
      swtAlert.error("Resend Failed", msg);
    } finally {
      setIsSending(false);
    }
  };

  if (sentTo) {
    return (
      <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-left text-sm text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200">
        <p>
          We&apos;ve sent a fresh verification link to{" "}
          <span className="font-medium">{sentTo}</span>. Please check your
          inbox (and spam folder).
        </p>
        <button
          type="button"
          onClick={() => {
            setSentTo("");
            setEmail("");
          }}
          className="cursor-pointer underline hover:no-underline"
        >
          Send to a different email
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2 text-left">
      <Label htmlFor="resendEmail" className="text-sm">
        Didn&apos;t get a working link? Resend the verification email:
      </Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id="resendEmail"
          name="resendEmail"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError("");
          }}
          aria-invalid={!!error}
          disabled={isSending}
          className="flex-1"
        />
        <Button type="submit" disabled={isSending}>
          {isSending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending…
            </>
          ) : (
            "Resend Email"
          )}
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </form>
  );
}

/* eslint-disable react/prop-types -- local presentational helper. */
function StatusIcon({ status }) {
  if (status === STATUS.LOADING || status === STATUS.PENDING) {
    return <Loader2 className="mx-auto h-12 w-12 animate-spin text-muted-foreground" />;
  }
  if (status === STATUS.SUCCESS) {
    return <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />;
  }
  if (status === STATUS.MISSING_TOKEN) {
    return <MailWarning className="mx-auto h-12 w-12 text-amber-500" />;
  }
  return <XCircle className="mx-auto h-12 w-12 text-destructive" />;
}
/* eslint-enable react/prop-types */

function titleFor(status) {
  if (status === STATUS.LOADING || status === STATUS.PENDING) {
    return "Verifying your email…";
  }
  if (status === STATUS.SUCCESS) return "Email Verified";
  if (status === STATUS.MISSING_TOKEN) return "Verification Link Missing";
  return "Verification Failed";
}

function messageFor(status, message) {
  if (status === STATUS.LOADING || status === STATUS.PENDING) {
    return "We're confirming your verification token with the server.";
  }
  if (status === STATUS.MISSING_TOKEN) {
    return "This page expects a verification token in the URL. Please open the link from the verification email we sent you.";
  }
  return message;
}
